import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Card, CardBody, Col, Row, TabContent, TabPane } from "reactstrap";

import { H3 } from "../../AbstractElements";
import { ALL_USERS_STATUS } from "../../Api/constants";
import JustifyTabs from "../../CommonElements/JustifyTabs";
import CatlogAssessmentHeading from "../../container/CatlogAssessmentHeading";
import ScheduledDetails from "../../container/ScheduledDetails";
import { useAppDispatch, useAppSelector } from "../../ReduxToolkit/Hooks";
import { getUsersList } from "../../ReduxToolkit/Reducers/UserSlice";
import {
  Active,
  CreatedBy,
  CreatedOn,
  NoOfQuestions,
  Past,
  Upcoming,
  StartDate,
  EndDate,
} from "../../utils/Constant";
import { getRandomInteger } from "../../utils/helper/helper";

const ScheduledSurveyDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [activeTab, setActiveTab] = useState("schedule_overview");
  const [userMap, setUserMap] = useState<{ [key: number]: any }>({});
  const { usersList } = useAppSelector(state => state.user);

  // Get survey data from location state or use dummy data
  const surveyData = location.state?.surveyData || {
    id: 1,
    name: "Employee Engagement Survey",
    title: "Annual Employee Engagement",
    description: "Annual engagement survey for all employees",
    created_by: 71778,
    created_on: "2024-06-01T10:00:00Z",
    questions: 12,
    status: "ongoing",
    start_date: "2024-06-01T00:00:00Z",
    end_date: "2024-06-30T23:59:59Z",
    assessment_type: "survey",
    question_bank_id: 1,
    module_count: 3,
    overall_question_count: 12,
  };

  // Dummy scheduled data - replace with actual API data
  const scheduledDataMap = {
    current_data: [
      {
        id: 1,
        startDate: "2024-06-01T00:00:00Z",
        endDate: "2024-06-30T23:59:59Z",
        statusLabel: "Ongoing",
        statusNumber: "80%",
        badgesLabel: "Responses",
        badgesValue: "120",
        icons: [{ name: "VisibilityOutlined", size: 24 }],
      },
    ],
    upcoming_data: [
      {
        id: 2,
        startDate: "2024-07-01T00:00:00Z",
        endDate: "2024-07-15T23:59:59Z",
        statusLabel: "Upcoming",
        statusNumber: "0%",
        badgesLabel: "Participants",
        badgesValue: "200",
        icons: [{ name: "EditOutlined", size: 24 }],
      },
    ],
    past_data: [
      {
        id: 3,
        startDate: "2024-05-01T00:00:00Z",
        endDate: "2024-05-31T23:59:59Z",
        statusLabel: "Completed",
        statusNumber: "90%",
        badgesLabel: "Responses",
        badgesValue: "45",
        icons: [{ name: "VisibilityOutlined", size: 24 }],
      },
    ],
  };

  const tabs = [
    { id: "schedule_overview", label: "Schedule Overview" },
    { id: "responses", label: "Responses" },
    { id: "analytics", label: "Analytics" },
  ];

  useEffect(() => {
    dispatch(getUsersList({ role: ALL_USERS_STATUS }));
  }, [dispatch]);

  useEffect(() => {
    const userMapObj = {};
    usersList?.users?.forEach(user => {
      userMapObj[user.id] = user;
    });
    setUserMap(userMapObj);
  }, [usersList]);

  const handleTabClick = tabId => {
    setActiveTab(tabId);
  };

  const getDetailBtn = () => {
    return (
      <div className="d-flex gap-2">
        <button
          className="btn btn-outline-primary"
          onClick={() =>
            navigate("/schedule-survey", { state: { surveyData } })
          }
        >
          Schedule Survey
        </button>
      </div>
    );
  };

  const onDeleteSchedule = id => {
    // TODO: Implement delete schedule functionality
    console.info("Delete schedule:", id);
  };

  const onEditClick = (id, type) => {
    // TODO: Implement edit schedule functionality
    console.info("Edit schedule:", id, type);
  };

  return (
    <div className="page-body scheduled-survey-details">
      <CatlogAssessmentHeading
        heading={surveyData?.name}
        headerChildren={getDetailBtn()}
        badgeText=""
        rows={[
          {
            columns: [
              {
                assign: "Survey Name",
                name: surveyData?.name || "",
                key: getRandomInteger(1, 10000),
              },
              {
                assign: "Title",
                name: surveyData?.title || "",
                key: getRandomInteger(1, 10000),
              },
              {
                assign: "Description",
                name: surveyData?.description || "",
                key: getRandomInteger(1, 10000),
              },
              {
                assign: NoOfQuestions,
                name: surveyData?.questions || 0,
                key: getRandomInteger(1, 10000),
              },
            ],
          },
          {
            columns: [
              {
                assign: CreatedBy,
                name: userMap[surveyData?.created_by]?.user_name || "-",
                key: getRandomInteger(1, 10000),
              },
              {
                assign: CreatedOn,
                name: new Date(surveyData?.created_on).toLocaleDateString(),
                key: getRandomInteger(1, 10000),
              },
              {
                assign: StartDate,
                name: new Date(surveyData?.start_date).toLocaleDateString(),
                key: getRandomInteger(1, 10000),
              },
              {
                assign: EndDate,
                name: new Date(surveyData?.end_date).toLocaleDateString(),
                key: getRandomInteger(1, 10000),
              },
            ],
          },
        ]}
      />

      <Card className="mt-3">
        <CardBody>
          <Row className="d-flex flex-column flex-md-row justify-content-between align-items-center">
            <Col
              lg="6"
              md="12"
              sm="12"
              xs="12"
              className="mb-1 mb-sm-0 d-flex flex-column gap-2"
            >
              <JustifyTabs
                tabs={tabs}
                activeTab={activeTab}
                onTabClick={handleTabClick}
              />
            </Col>
          </Row>
        </CardBody>
      </Card>

      <TabContent activeTab={activeTab}>
        <TabPane tabId="schedule_overview">
          {activeTab === "schedule_overview" && (
            <div className="mb-5">
              <div className="mt-3">
                <ScheduledDetails
                  onDeleteClick={id => {
                    onDeleteSchedule(id);
                  }}
                  onEditClick={id => {
                    onEditClick(id, "current");
                  }}
                  title={Active}
                  rows={scheduledDataMap?.current_data}
                  showDelete={false}
                />
              </div>
              <div className="mt-3">
                <ScheduledDetails
                  onDeleteClick={id => {
                    onDeleteSchedule(id);
                  }}
                  onEditClick={id => {
                    onEditClick(id, "upcoming");
                  }}
                  title={Upcoming}
                  rows={scheduledDataMap?.upcoming_data}
                />
              </div>
              <div className="mt-3">
                <ScheduledDetails
                  onDeleteClick={id => {
                    onDeleteSchedule(id);
                  }}
                  onEditClick={id => {
                    onEditClick(id, "past");
                  }}
                  title={Past}
                  rows={scheduledDataMap?.past_data}
                  showDelete={false}
                />
              </div>
            </div>
          )}
        </TabPane>
        <TabPane tabId="responses">
          <div className="mt-3">
            <H3>Survey Responses</H3>
            <p>Responses tab content will be implemented here.</p>
          </div>
        </TabPane>
        <TabPane tabId="analytics">
          <div className="mt-3">
            <H3>Survey Analytics</H3>
            <p>Analytics tab content will be implemented here.</p>
          </div>
        </TabPane>
      </TabContent>
    </div>
  );
};

export default ScheduledSurveyDetails;
